import { Injectable } from '@angular/core';

import { environment } from '../../environments/environment';
import { Lot, LotPricingByCode } from './lot.model';
import { AppHttpClientService, AppHttpClientConfig } from '../shared/app-http-client.service';
import { parseToNumber } from '../shared/funcs/parser-helpers';

@Injectable()
export class LotService {
  constructor(private appHttpClient: AppHttpClientService) {}

  getAllLots(auctionId: string) {
    return this.appHttpClient.get<Array<Lot>>('lots?auctionId=' + auctionId).toPromise();
  }

  getLotByCode(auctionId: string, code: string): Promise<Lot | null> {
    const path = 'lots/' + encodeURIComponent(code) + '?auctionId=' + encodeURIComponent(auctionId);
    return this.appHttpClient.getOrNoContent<Lot>(path).toPromise();
  }

  getLotPricingByCode(auctionId: string, code: string): Promise<LotPricingByCode | null> {
    const path =
      'lots/' +
      encodeURIComponent(code) +
      '/pricing?auctionId=' +
      encodeURIComponent(auctionId);
    return this.appHttpClient.getOrNoContent<LotPricingByCode>(path).toPromise();
  }

  importLot(auctionId: string, lots: Lot[], shouldRemoveAll: boolean) {
    const lotsPayload = lots.map((lot) => this.mapLotToImportRequest(lot));
    return this.appHttpClient.post('lots/import', {
      auctionId,
      lots: lotsPayload,
      clearAll: shouldRemoveAll,
    });
  }

  deleteAllLots(auctionId: string) {
    return this.appHttpClient.delete('lots', '', 'auctionId=' + auctionId);
  }

  deleteAllBids(auctionId: string) {
    return this.appHttpClient.delete('bids', '', 'auctionId=' + auctionId);
  }

  deleteLot(code: string, auctionId: string) {
    return this.appHttpClient.delete('lots', code, 'auctionId=' + auctionId);
  }

  deleteLotBids(code: string, auctionId: string) {
    return this.appHttpClient.delete('bids', code, 'auctionId=' + auctionId);
  }

  saveLot(auctionId: string, lot: Lot) {
    return this.appHttpClient.post('lots', { auctionId, lot });
  }

  updateLot(auctionId: string, lot: Lot) {
    return this.appHttpClient.put('lots', { auctionId, lot });
  }

  flagAsEdition(auctionId: string, lotId: string, lotActiveId: string) {
    if (lotId !== lotActiveId) {
      return;
    }

    return this.appHttpClient.put('auction/lot/editing', { auctionId, lotId });
  }

  flagAsSaved(auctionId: string, lotId: string, lotActiveId: string) {
    if (lotId !== lotActiveId) {
      return;
    }

    return this.appHttpClient.put('auction/lot/notEditing', { auctionId, lotId });
  }

  invalidateClientCache(auctionId: string) {
    const config = new AppHttpClientConfig();
    config.backendUrl = environment.backend.widget;
    return this.appHttpClient.post('lots/reload', { auctionId }, config);
  }

  private mapLotToImportRequest(lot: Lot) {
    return {
      code: lot.code,
      sex: lot.sex,
      quantity: lot.quantity,
      avgWeight: lot.avgWeight,
      totalWeight: lot.totalWeight,
      origin: lot.origin ?? '',
      observations: lot.observations ?? '',
      date: lot.date,
      video: lot.video ?? '',
      providerName: lot.providerName ?? '',
      providerPhone: lot.providerPhone ?? '',
      basePrice: parseToNumber(lot.basePrice, -1),
      increment: parseToNumber(lot.increment, -1),
      auctionLotType: lot.auctionLotType ?? null,
    };
  }
}
